import { Combobox } from "@sentricmusic/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useElasticQuery } from "@/api/mutations";

import { indexReducedPattern } from "./constants";

export default function SongFilter({ accounts, writers, values, onValuesChange }) {
  const { t } = useTranslation("common");
  const { mutate: searchSongs } = useElasticQuery();

  const [songOptions, setSongOptions] = useState([]);
  const [isSongLoading, setIsSongLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const debounceRef = useRef(null);

  const fetchSongs = useCallback((accts, writerCodes = [], search = "") => {
    if (!accts?.length)
      return;

    setIsSongLoading(true);

    const mustClause = search.trim()
      ? {
          match_phrase_prefix: {
            songTitle: { query: search, max_expansions: 50 },
          },
        }
      : { match_all: {} };

    const filters = [{ terms: { artistAccountCode: accts } }];
    if (writerCodes?.length) {
      filters.push({ terms: { writerCode: writerCodes } });
    }

    searchSongs({
      index: indexReducedPattern,
      body: {
        query: {
          bool: {
            must: mustClause,
            filter: filters,
          },
        },
        collapse: { field: "songCode" },
      },
      size: 50,
    }, {
      onSuccess: (data) => {
        const hits = data?.body?.hits?.hits || [];
        const optionsMap = new Map();

        hits.forEach((hit) => {
          const source = hit?._source || {};
          const songCode = source.songCode;
          const songTitle = source.songTitle;
          if (!songCode || optionsMap.has(songCode))
            return;
          optionsMap.set(songCode, songTitle || songCode);
        });

        setSongOptions(Array.from(optionsMap, ([value, label]) => ({ value, label })));
        setIsSongLoading(false);
      },
      onError: () => {
        setSongOptions([]);
        setIsSongLoading(false);
      },
    });
  }, [searchSongs]);

  useEffect(() => {
    hasFetchedRef.current = false;
    setSongOptions([]);
  }, [accounts, writers]);

  const handleFocus = useCallback(() => {
    if (hasFetchedRef.current || !accounts?.length)
      return;
    hasFetchedRef.current = true;
    fetchSongs(accounts, writers);
  }, [fetchSongs, accounts, writers]);

  const handleSongsSearch = useCallback((search) => {
    if (debounceRef.current)
      clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSongs(accounts, writers, search);
    }, 500);
  }, [fetchSongs, accounts, writers]);

  useEffect(() => {
    return () => {
      if (debounceRef.current)
        clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div onFocus={handleFocus}>
      <Combobox
        customWidth
        triggerClassName="w-full"
        label={t("other:song", "Song")}
        placeholder={t("other:select-song", "Select song...")}
        searchPlaceholder={t("other:search-song", "Search song...")}
        data={songOptions}
        values={values}
        onValuesChange={onValuesChange}
        onSearchChange={handleSongsSearch}
        selectAllLabel={t("common:select-all", "Select all")}
        clearButton
        clearLabel={t("other:clear", "Clear")}
        isLoading={isSongLoading}
        disabled={!accounts?.length}
      />
    </div>
  );
}
